import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/external-client';
import { EXTERNAL_SUPABASE_URL } from '@/config/externalSupabase';
import { invokeEdgeFunction } from '@/lib/edgeFunctions';
import { toast } from '@/hooks/use-toast';
import {
  CUSTOMER_SESSION_KEY,
  FORCE_SIGNED_OUT_KEY,
  SESSION_INVALIDATED_EVENT,
  getCustomerSessionId,
  setCustomerSessionId,
} from '@/lib/customerSession';
import {
  getLastRegisteredFcm,
  mintFreshFcmToken,
  postRegisterFcmToken,
  setLastRegisteredFcm,
} from '@/lib/customerFcm';

interface CustomerProfile {
  id: string;
  full_name: string;
  phone: string;
  customer_id: string | null;
  email: string | null;
  photo_url: string | null;
  rating: number | null;
  onboarding_completed: boolean;
}

interface CustomerAuthContextType {
  customer: CustomerProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  sendOtp: (phone: string) => Promise<{ success: boolean; error?: string; customerName?: string; isNewCustomer?: boolean; otpDebug?: string }>;
  verifyOtp: (phone: string, otp: string, fullName?: string) => Promise<{ success: boolean; error?: string; isNewCustomer?: boolean }>;
  signOut: () => Promise<void>;
  refreshCustomer: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const forcedOutRef = useRef<boolean>(false);
  const sessionChannelRef = useRef<any>(null);
  // True only while an intentional sign-out is in flight. Prevents a spurious
  // SIGNED_OUT (transient token-refresh failure) from logging the customer out.
  const intentionalSignOutRef = useRef<boolean>(false);

  // ── Single-device session enforcement ──
  // Mirrors the driver app. Compares the locally stored session id against the
  // server's `customers.active_session_id`. If they differ, the customer was
  // logged in on another device — clear local auth and bounce to /customer/login.
  const forceSignOutForOtherDevice = useCallback(async () => {
    if (forcedOutRef.current) return;
    forcedOutRef.current = true;
    try { sessionStorage.setItem(FORCE_SIGNED_OUT_KEY, '1'); } catch { /* ignore */ }
    if (sessionChannelRef.current) {
      try { supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
      sessionChannelRef.current = null;
    }

    // Clear Supabase auth keys SYNCHRONOUSLY before navigating.
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => { try { localStorage.removeItem(k); } catch {} });
    await setCustomerSessionId(null);
    setLastRegisteredFcm(null);
    setCustomer(null);

    intentionalSignOutRef.current = true;
    try { supabase.auth.signOut().catch(() => {}); } catch { /* ignore */ }
    try {
      toast({
        title: 'Signed out',
        description: 'Your account was used on another device.',
        variant: 'destructive',
      });
    } catch { /* ignore */ }
    if (typeof window !== 'undefined' && window.location.pathname !== '/customer/login') {
      window.location.assign('/customer/login');
    }
  }, []);

  useEffect(() => {
    // Register listener FIRST so we never miss the INITIAL_SESSION event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const isCustomerSession = session?.user?.user_metadata?.role === 'customer';
      const customerId = session?.user?.user_metadata?.customer_id as string | undefined;
      const onboardingCompleted = !!session?.user?.user_metadata?.onboarding_completed;

      // Honor the force-signed-out marker — don't re-hydrate a stale session.
      let blockedByForce = false;
      try {
        if (sessionStorage.getItem(FORCE_SIGNED_OUT_KEY)) blockedByForce = true;
      } catch { /* ignore */ }

      if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && isCustomerSession && customerId && !blockedByForce) {
        loadCustomerProfile(customerId, onboardingCompleted).finally(() => {
          if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
            setLoading(false);
          }
        });
        // Background validation (don't block UI).
        validateServerSession(customerId);
      } else if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        if (!isCustomerSession || blockedByForce) {
          setCustomer(null);
        }
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        if (intentionalSignOutRef.current) {
          intentionalSignOutRef.current = false;
          setCustomer(null);
        } else {
          // Spurious SIGNED_OUT from a transient token-refresh failure.
          // Attempt recovery before clearing state.
          console.warn('[customer-auth] ignoring spurious SIGNED_OUT — attempting token recovery');
          supabase.auth.refreshSession().catch(() => {
            setCustomer(null);
          });
        }
      }
    });

    // Listen for the global session_invalidated signal (from edge call sites
    // that opt-in via handleEdgeResult).
    const onInvalidated = () => { forceSignOutForOtherDevice(); };
    window.addEventListener(SESSION_INVALIDATED_EVENT, onInvalidated as EventListener);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(SESSION_INVALIDATED_EVENT, onInvalidated as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced server-authoritative session check via dispatch-trip edge fn.
  const lastCheckRef = useRef<number>(0);
  const validateServerSession = useCallback(async (customerId: string) => {
    if (forcedOutRef.current) return;
    const stored = await getCustomerSessionId();
    if (!stored) return;
    const now = Date.now();
    if (now - lastCheckRef.current < 30000) return;
    lastCheckRef.current = now;
    try {
      const { data } = await invokeEdgeFunction('dispatch-trip', {
        action: 'validate_customer_session',
        customer_id: customerId,
        session_id: stored,
      });
      if (data?.success && data.valid === false) {
        forceSignOutForOtherDevice();
      }
    } catch { /* network error — realtime will catch up */ }
  }, [forceSignOutForOtherDevice]);

  // Subscribe to realtime + resume signals while the customer is signed in.
  useEffect(() => {
    if (!customer?.id) return;
    const customerId = customer.id;
    let cancelled = false;
    let pgChannel: any = null;
    let bcChannel: any = null;
    let resubscribeTimer: ReturnType<typeof setTimeout> | null = null;

    const handleUpdate = async (payload: any) => {
      const next = payload?.new?.active_session_id;
      const stored = await getCustomerSessionId();
      if (next && stored && next !== stored) {
        forceSignOutForOtherDevice();
      }
    };

    const subscribePg = () => {
      if (cancelled) return;
      if (sessionChannelRef.current) {
        try { supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
        sessionChannelRef.current = null;
      }
      pgChannel = supabase
        .channel(`customer-session-${customerId}`)
        .on('postgres_changes' as any, {
          event: 'UPDATE',
          schema: 'public',
          table: 'customers',
          filter: `id=eq.${customerId}`,
        }, handleUpdate)
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            validateServerSession(customerId);
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            if (resubscribeTimer) clearTimeout(resubscribeTimer);
            resubscribeTimer = setTimeout(subscribePg, 1500);
          }
        });
      sessionChannelRef.current = pgChannel;
    };
    subscribePg();

    // Broadcast fallback (works without RLS on customers).
    try {
      bcChannel = supabase
        .channel(`customer-session:${customerId}`)
        .on('broadcast' as any, { event: 'session_replaced' }, async (msg: any) => {
          const next = msg?.payload?.new_session_id;
          const stored = await getCustomerSessionId();
          if (next && stored && next !== stored) {
            forceSignOutForOtherDevice();
          }
        })
        .subscribe();
    } catch { /* ignore */ }

    const removers: Array<() => void> = [];
    const onVisible = () => { if (document.visibilityState === 'visible') validateServerSession(customerId); };
    const onFocus = () => validateServerSession(customerId);
    const onOnline = () => validateServerSession(customerId);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    removers.push(() => document.removeEventListener('visibilitychange', onVisible));
    removers.push(() => window.removeEventListener('focus', onFocus));
    removers.push(() => window.removeEventListener('online', onOnline));

    (async () => {
      try {
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const sub = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) validateServerSession(customerId);
        });
        removers.push(() => { try { sub.remove(); } catch {} });
      } catch { /* ignore */ }
    })();

    return () => {
      cancelled = true;
      if (resubscribeTimer) clearTimeout(resubscribeTimer);
      try { if (sessionChannelRef.current) supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
      sessionChannelRef.current = null;
      try { if (bcChannel) supabase.removeChannel(bcChannel); } catch { /* ignore */ }
      bcChannel = null;
      for (const r of removers) { try { r(); } catch {} }
    };
  }, [customer?.id, forceSignOutForOtherDevice, validateServerSession]);

  const loadCustomerProfile = async (customerId: string, onboardingCompletedFromAuth = false) => {
    const { data } = await supabase
      .from('customers')
      .select('id, full_name, phone, customer_id, email, photo_url, rating, emergency_contact_person_name')
      .eq('id', customerId)
      .single();

    if (data) {
      setCustomer({
        id: data.id,
        full_name: data.full_name,
        phone: data.phone,
        customer_id: data.customer_id,
        email: data.email,
        photo_url: data.photo_url,
        rating: data.rating,
        onboarding_completed: onboardingCompletedFromAuth || !!data.emergency_contact_person_name,
      });
    }
  };

  const refreshCustomer = async () => {
    if (customer?.id) {
      const { data: authUser } = await supabase.auth.getUser();
      await loadCustomerProfile(customer.id, !!authUser.user?.user_metadata?.onboarding_completed);
    }
  };

  const sendOtp = async (phone: string) => {
    const { data, error } = await invokeEdgeFunction('customer-login', {
      phone, action: 'send_otp',
    });

    if (error || !data?.success) {
      return { success: false, error: data?.error || error || 'Failed to send OTP' };
    }

    return { 
      success: true, 
      customerName: data.customer_name, 
      isNewCustomer: data.is_new_customer,
      otpDebug: data.otp_debug,
    };
  };

  const verifyOtp = async (phone: string, otp: string, fullName?: string) => {
    // Mint a fresh FCM token BEFORE invoking verify_otp so customer-login
    // can persist it atomically with the new active_session_id. Mirrors
    // the Driver App — Verify OTP is the single authoritative point of
    // token creation. Android-native only; null on web / iOS.
    const minted = await mintFreshFcmToken();

    const { data, error } = await invokeEdgeFunction('customer-login', {
      phone,
      action: 'verify_otp',
      otp_code: otp,
      full_name: fullName,
      fcm_token: minted?.token,
      platform: minted?.platform,
    });

    if (error || !data?.success) {
      return { success: false, error: data?.error || error || 'Verification failed' };
    }

    const expiresAt = data.expires_at || (() => {
      try {
        const payload = JSON.parse(atob(String(data.access_token).split('.')[1] || ''));
        return typeof payload?.exp === 'number' ? payload.exp : Math.floor(Date.now() / 1000) + 3600;
      } catch {
        return Math.floor(Date.now() / 1000) + 3600;
      }
    })();

    let sessionErr: unknown = null;
    try {
      const sessionResult = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      sessionErr = sessionResult.error;
    } catch (err) {
      sessionErr = err;
    }

    if (sessionErr) {
      try {
        const storageKey = `sb-${new URL(EXTERNAL_SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
        const tokenValue = JSON.stringify({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: expiresAt,
          expires_in: Math.max(0, expiresAt - Math.floor(Date.now() / 1000)),
          token_type: 'bearer',
          user: {
            id: data.auth_user_id || data.user_id,
            aud: 'authenticated',
            role: 'authenticated',
            email: `customer_${phone.replace(/\+/g, '')}@customer.internal`,
            user_metadata: {
              full_name: data.customer?.full_name,
              role: 'customer',
              customer_id: data.customer?.id,
              phone,
              onboarding_completed: !!data.customer?.onboarding_completed,
            },
            app_metadata: { provider: 'email', providers: ['email'] },
          },
        });
        // Write to Capacitor Preferences on native so the token survives APK updates
        if (Capacitor.isNativePlatform()) {
          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.set({ key: storageKey, value: tokenValue });
        } else {
          localStorage.setItem(storageKey, tokenValue);
        }
      } catch {
        return { success: false, error: 'Failed to establish session' };
      }
    }

    // Persist single-device session id and reset the force-signed-out marker.
    if (data.session_id) {
      await setCustomerSessionId(data.session_id);
    }
    forcedOutRef.current = false;
    try { sessionStorage.removeItem(FORCE_SIGNED_OUT_KEY); } catch { /* ignore */ }

    // Record the token the server just registered so the safety-net
    // diff-gate in useFcmRegistration skips the next registration
    // callback (Android replays it with the same value).
    if (minted?.token && data.customer?.id) {
      setLastRegisteredFcm({ customerId: data.customer.id, token: minted.token });
    }

    // Post-login self-heal: if the inline mint returned null (slow Play
    // Services, transient FCM failure) the server marked `fcm_pending`
    // and currently has zero token rows for this customer. Retry in the
    // background until a token row exists. Bounded: 6 attempts, ~30s.
    if ((!minted?.token || data.fcm_pending) && data.customer?.id) {
      const customerId: string = data.customer.id;
      (async () => {
        for (let attempt = 0; attempt < 6; attempt++) {
          const last = getLastRegisteredFcm();
          if (last && last.customerId === customerId) return;
          const m = await mintFreshFcmToken();
          if (m?.token) {
            await postRegisterFcmToken(customerId, m.token);
            const after = getLastRegisteredFcm();
            if (after && after.customerId === customerId) return;
          }
          await new Promise((r) => setTimeout(r, 5000));
        }
        console.warn('[auth] post-login FCM self-heal gave up after 6 attempts');
      })();
    }

    const { data: authUser } = await supabase.auth.getUser();
    setCustomer({
      ...data.customer,
      onboarding_completed: !!authUser.user?.user_metadata?.onboarding_completed || !!data.customer?.onboarding_completed,
    });
    return { success: true, isNewCustomer: data.is_new_customer };
  };

  const signOut = async () => {
    // Remove this device's push token rows and detach Capacitor listeners
    if (customer?.id) {
      try {
        await supabase
          .from('push_tokens')
          .delete()
          .eq('user_id', customer.id)
          .eq('user_type', 'customer');
      } catch {}
    }
    if (Capacitor.isNativePlatform()) {
      try { await PushNotifications.removeAllListeners(); } catch {}
    }

    // Clear Supabase session keys from localStorage to prevent stale token refresh on next launch
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    await setCustomerSessionId(null);
    setLastRegisteredFcm(null);

    intentionalSignOutRef.current = true;
    await supabase.auth.signOut();
    setCustomer(null);
  };

  return (
    <CustomerAuthContext.Provider value={{
      customer,
      loading,
      isAuthenticated: !!customer,
      sendOtp,
      verifyOtp,
      signOut,
      refreshCustomer,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
}
